/**
 * Who sets up and manages agents. Admins add any member and manage agents and integrations up to the roles they grant.
 * Editors set up agents too, while the org lets them (`editors_add_agents`, on unless an admin turned it off), up to
 * their own role, and manage the ones they set up (even once it's turned off). An agent never has a higher role than
 * whoever set it up.
 */
import type { Member, Role } from "../api/types.gen.ts";
import { atLeast, grantable } from "./roles.ts";

/** Whether `role` (yours) may set up agents, given the org's setting for editors. */
export const canAddAgents = (role: Role, editorsAddAgents: boolean): boolean =>
  atLeast(role, "admin") || (editorsAddAgents && atLeast(role, "editor"));

/** Whether you (`me`, with `role`) set up `m` and manage it as such: while you're an editor, up to your role. */
const setUpBy = (m: Member, me: string | undefined, role: Role): boolean =>
  m.kind !== "human" && me !== undefined && m.set_up_by === me && atLeast(role, "editor") && atLeast(role, m.role);

/** Whether you (`me`, with `role`) manage member `m`: change, remove, connect them. Not yourself (you leave). */
export function manages(m: Member, me: string | undefined, role: Role): boolean {
  if (m.id === me) {
    return false;
  }
  return grantable(role).includes(m.role) || setUpBy(m, me, role);
}

/** The agents and integrations of `members` you may make keys for. */
export const managedBots = (members: readonly Member[] | undefined, me: string | undefined, role: Role): Member[] =>
  (members ?? []).filter((m) => m.kind !== "human" && (atLeast(role, "admin") || setUpBy(m, me, role)));

/** The roles `role` (yours) may give a new agent: an admin's grants (never owner), else up to your own. */
export function agentRoles(role: Role): Role[] {
  if (atLeast(role, "admin")) {
    return grantable(role, false);
  }
  return (["viewer", "editor"] as const).filter((r) => atLeast(role, r));
}

/**
 * The roles you may give `m` (whom you manage), among `members`: as for a new agent (or what you grant a person), never
 * above whoever set it up (while they're here); its own role stays listed (an agent still an owner, from before agents
 * were capped, can be moved down, not back up).
 */
export function rolesFor(m: Member, members: readonly Member[], role: Role): Role[] {
  const by = m.set_up_by ? members.find((x) => x.id === m.set_up_by) : undefined;
  const mine = m.kind === "human" ? grantable(role) : agentRoles(role);
  const capped = by ? mine.filter((r) => atLeast(by.role, r)) : mine;
  return capped.includes(m.role) ? capped : [...capped, m.role];
}

/** Who set up `m`, by name, when it's an agent or integration someone here set up. */
export function setUpByName(m: Member, members: readonly Member[]): string | undefined {
  return m.set_up_by ? members.find((x) => x.id === m.set_up_by)?.name : undefined;
}
